import { axiosInstance } from './api';

// AI197 (#1165) — «график порога полки»: распределение eff = сырое/делитель (без обрезки
// на 1) по бинам 0.05, столбцы разрезаны по статусу итогового алерта, под бинами — фото
// полки. См. services/services/api/calibration_chart.py.

/** Статус итогового алерта по кадру. `none` — алерт не отправлялся. */
export type AlertAnswer =
  | 'FULL' | 'TIDY_BUT_LOW_STOCK' | 'PARTIALLY_EMPTY' | 'NOTICEABLY_EMPTY' | 'ALMOST_EMPTY' | 'none';

export const ANSWER_ORDER: AlertAnswer[] = [
  'FULL', 'TIDY_BUT_LOW_STOCK', 'PARTIALLY_EMPTY', 'NOTICEABLY_EMPTY', 'ALMOST_EMPTY', 'none',
];

/** Подписи статусов. Нигде не пишем «валидатор» (#1168) — только статус итогового алерта.
 *  Без «алерт отменён/подтверждён» (Дмитрий 02.10): человек на любое сообщение (смена статуса,
 *  плановая проверка, пересъёмка) отмечает, что видит на стеллаже, — «полная» чаще всего ответ
 *  на «пополнили?», а не отмена алерта. Заголовок легенды — «Статус итогового алерта:». */
export const ANSWER_LABEL: Record<AlertAnswer, string> = {
  FULL: 'полная',
  TIDY_BUT_LOW_STOCK: 'аккуратно, мало товара',
  PARTIALLY_EMPTY: 'частично пустая',
  NOTICEABLY_EMPTY: 'заметно пустая',
  ALMOST_EMPTY: 'почти пустая',
  none: 'не проверялся',
};

export const ANSWER_COLOR: Record<AlertAnswer, string> = {
  FULL: '#22c55e',
  TIDY_BUT_LOW_STOCK: '#eab308',
  PARTIALLY_EMPTY: '#f97316',
  NOTICEABLY_EMPTY: '#ef4444',
  ALMOST_EMPTY: '#991b1b',
  none: '#94a3b8',
};

export type ShelfModel = 'pct' | 'yolo';
export const MODEL_LABEL: Record<ShelfModel, string> = { yolo: 'количественная', pct: 'процентная' };

export interface ShelfChartPhoto {
  frame_id: number;
  at: string;
  raw: number;
  eff: number;
  answer: AlertAnswer;
  /** Для алертных статусов: была ли ниже порога только эта полка (null, если алерта не было). */
  single: boolean | null;
  url: string;
  /** Боксы детектора в долях кропа полки, четырёхугольники [[x,y]×4]. Только количественная модель. */
  boxes: number[][][];
  /** Контур полки в долях картинки (кроп стеллажа у количественной полки), [[x,y]×4]; null — нет. */
  outline?: number[][] | null;
}

export interface ShelfChartBin {
  /** Абсолютные (сырые) границы бина; eff_* — те же после делителя, только подпись (#1184). */
  lo: number;
  hi: number;
  eff_lo: number;
  eff_hi: number;
  counts: Partial<Record<AlertAnswer, number>>;
  single_counts: Partial<Record<AlertAnswer, number>>;
  photos: ShelfChartPhoto[];
}

export interface ShelfChartResponse {
  shelf: {
    id: number; name: string; rack_id: number; rack_name: string; zone_id: number;
    model: ShelfModel;
    shelf_predict_percentage: boolean | null;
    rack_predict_percentage: boolean;
  };
  thresholds: {
    empty_shelf_score: number;
    /** Порог в абсолютных = empty_shelf_score × divisor — где он стоит на оси. */
    threshold_abs: number;
    model_correction_max_pct: number | null;
    useful_area_pct: number | null;
    divisor: number;
  };
  window: { from: string; to: string };
  mode: 'alerts' | 'all';
  /** Модель, ПО КОТОРОЙ построен график (current → эффективная модель полки, other → другая). */
  model: ShelfModel;
  periods: { model: ShelfModel; from: string; to: string; n: number }[];
  mixed: boolean;
  switch_at: string | null;
  model_deployed_at: string | null;
  step: number;
  bins: ShelfChartBin[];
  stats: { n: number; below_threshold: number; share_below: number | null; answered: number };
}

export interface ShelfChartParams {
  shelf_id: number;
  days?: number;
  from?: string;
  to?: string;
  mode?: 'alerts' | 'all';
  model?: 'current' | 'other';
  photos?: number;
}

export const getShelfChart = (params: ShelfChartParams) =>
  axiosInstance.get<ShelfChartResponse>('/core/api/analytics/calibration-shelf-chart/', { params });

// ── Столы и очередь (#1168) ─────────────────────────────────────────────────────
export type TrackAnswer = 'confirmed' | 'cancelled' | 'pending' | 'none';
export const TRACK_ANSWER_ORDER: TrackAnswer[] = ['confirmed', 'cancelled', 'pending', 'none'];
export const TRACK_ANSWER_LABEL: Record<TrackAnswer, string> = {
  confirmed: 'алерт подтверждён',
  cancelled: 'алерт отменён',
  pending: 'алерт без ответа',
  none: 'алерт не отправлялся',
};
export const TRACK_ANSWER_COLOR: Record<TrackAnswer, string> = {
  confirmed: '#ef4444',
  cancelled: '#22c55e',
  pending: '#eab308',
  none: '#94a3b8',
};

export interface TrackChartPhoto {
  frame_id: number;
  at: string;
  x: number;
  /** Пересчёт очереди: число на этом кадре, как записано при съёмке (старым полигоном). */
  x_orig?: number;
  /** Пересчёт очереди: максимум минуты — по новому полигону и как записано. */
  minute_x?: number;
  minute_x_orig?: number;
  /** Пересчёт очереди: это кадр, по которому ушёл алерт «длинная очередь». */
  is_alert_frame?: boolean;
  /** Стол/очередь — TrackAnswer; стеллаж — статус ответа на алерт стеллажа (как у полки). */
  answer: TrackAnswer | AlertAnswer;
  url: string;
  /** counted — пересчёт очереди: ноги бокса в новом полигоне. */
  boxes: { x0: number; y0: number; x1: number; y1: number; counted?: boolean }[];
  polygons: number[][][];
  /** Стол: состояние по модели (с поправкой человека). */
  state?: string;
}
export interface TrackChartBin {
  lo: number;
  hi: number;
  label: string | null;
  counts: Partial<Record<string, number>>;
  photos: TrackChartPhoto[];
}
export interface TrackChartResponse {
  kind: 'table' | 'queue' | 'rack';
  entity: { id: number; name: string };
  x: 'score' | 'people' | 'pct';
  /** Стол: по какому скору построено — food | dirty | max. */
  score?: 'food' | 'dirty' | 'native' | 'max';
  x_label: string;
  threshold: number | null;
  threshold_label: string;
  threshold_note: string;
  /** Стол: порог свой (true) или системный (AI214). */
  threshold_custom?: boolean;
  bins: TrackChartBin[];
  /** Стол: распределение по 4 состояниям — всегда, и для строк без скоров. */
  states: { state: string; label: string; counts: Partial<Record<TrackAnswer, number>> }[] | null;
  /** Стол: первый замер со скорами в окне (null — скоров ещё нет). */
  scores_from: string | null;
  stats: {
    n: number; answered: number; scored?: number; above?: number;
    /** Пересчёт очереди: минут выше порога по записанным числам (для сравнения с above). */
    above_orig?: number;
    /** Стол (AI212): доля кадров со скорами в серой зоне 20–80 % (ни «чисто», ни «грязно») и сама зона. */
    uncertain_share?: number | null; uncertain_band?: [number, number]; unconfident?: boolean;
  };
  window: { from: string; to: string };
  mode: 'alerts' | 'all';
  /** Только пересчёт очереди по боксам (calibration-queue-recount). */
  recount?: QueueRecountInfo;
}
export interface TrackChartParams {
  kind: 'table' | 'queue' | 'rack';
  id: number;
  days?: number;
  from?: string;
  to?: string;
  mode?: 'alerts' | 'all';
  photos?: number;
  /** Стол: какой скор по оси — «еда» или «грязная посуда» (две гистограммы, #1376); без параметра — max. */
  score?: 'food' | 'dirty' | 'native';
}
export const getTrackChart = (params: TrackChartParams) =>
  axiosInstance.get<TrackChartResponse>('/core/api/analytics/calibration-track-chart/', { params });

// ── Очередь: пересчёт по боксам под текущий/черновой полигон (Алексей 21.10) ─────
// См. services/services/api/calibration_queue_recount.py. Правило — как у occupancy_engine:
// ноги бокса (середина нижней кромки) в залитом контуре полигона, на пиксельной сетке кадра.

export interface QueueRecountAlert {
  at: string;
  answer: TrackAnswer;
  /** Кадр, по которому ушёл алерт; null — кадра в окне не нашлось. */
  frame_id: number | null;
  orig: number | null;
  new: number | null;
  /** Ушёл бы по новому полигону (то же правило стабильности, текущий порог). */
  would_fire: boolean | null;
  orig_fire: boolean | null;
}
export interface QueueRecountInfo {
  /** true — считали по черновику (не сохранён). */
  draft: boolean;
  frames: number;
  frames_changed: number;
  minutes_changed: number;
  /** Кадры, у которых боксы людей не сохранились — взято записанное число. */
  no_boxes: number;
  score_threshold: number;
  frame_size: Record<string, { w: number; h: number; known: boolean }>;
  /** Начало пересчёта (в «все замеры» сужается до последних max_frames кадров). */
  from: string;
  truncated: boolean;
  max_frames: number;
  alerts: QueueRecountAlert[];
}
export interface QueueRecountParams {
  id: number;
  from?: string;
  to?: string;
  mode?: 'alerts' | 'all';
  photos?: number;
  /** Черновик: {area_id: [[x, y], …]} — ничего не сохраняет. Без него — сохранённый полигон. */
  polygons?: Record<string, number[][]> | null;
}
export const postQueueRecount = (params: QueueRecountParams) =>
  axiosInstance.post<TrackChartResponse>('/core/api/analytics/calibration-queue-recount/', params);
